export default function SQLVEditSupport() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            Support - SQL Viewer &amp; Editor
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Help, troubleshooting, and contact details for SQL Viewer &amp;
            Editor.
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <h2 className="text-2xl font-bold text-[#A8720E]">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I open a database file?
              </h3>
              <p className="mt-1">
                Tap the + button on the home screen and choose &ldquo;Open
                existing database&rdquo;. Select a .db, .sqlite, or .sqlite3
                file from your device. The file is opened locally and is never
                uploaded to any server.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I create a new database?
              </h3>
              <p className="mt-1">
                Tap the + button on the home screen and choose &ldquo;Create new
                database&rdquo;. Give it a name and start creating tables and
                inserting data using the visual editor or SQL queries.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I run SQL queries?
              </h3>
              <p className="mt-1">
                Open a database, then tap the SQL Editor option from the menu.
                Type your query and tap Run. Results are displayed in a
                scrollable table. You can run SELECT, INSERT, UPDATE, DELETE,
                CREATE TABLE, and other standard SQL statements.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I browse and edit table data?
              </h3>
              <p className="mt-1">
                Open a database and tap on any table card to browse its rows.
                You can search, sort, add new rows, edit existing rows, and
                delete rows directly from the table view.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I export data as CSV?
              </h3>
              <p className="mt-1">
                Open a table or run a query, then tap the Export button. Choose
                CSV as the export format. The file will be saved to your device
                or shared via the system share sheet.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I import data from CSV?
              </h3>
              <p className="mt-1">
                Open a database, tap the menu, and select Import CSV. Choose a
                CSV file from your device. The app will map columns to your
                table and import the rows.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I change the language?
              </h3>
              <p className="mt-1">
                Go to Settings and tap Language. SQL Viewer &amp; Editor
                supports 15 languages including English, Hindi, Spanish,
                Portuguese, Arabic, Chinese, Japanese, Korean, and more.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Why does the app show ads?
              </h3>
              <p className="mt-1">
                Ads help support continued development. You can remove all ads
                with a one-time in-app purchase from Settings.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                The app is crashing. What do I do?
              </h3>
              <p className="mt-1">
                Try closing and reopening the app. Make sure you have the latest
                version from the App Store or Google Play. If the crash happens
                when opening a specific database file, the file may be corrupted.
                Email us with your device model, OS version, app version, and
                steps to reproduce.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Contact Support
          </h2>
          <p>
            For help, bug reports, or privacy-related questions, email{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            . We usually respond within 24-48 hours.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Report a Bug</h2>
          <p>
            If something is not working as expected, include these details in
            your email:
          </p>
          <ul className="list-disc space-y-1 pl-6">
            <li>Your device model and OS version</li>
            <li>Your app version (found in Settings &gt; About)</li>
            <li>What you were trying to do</li>
            <li>The error or unexpected result</li>
            <li>Steps to reproduce the issue, if known</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Important Links
          </h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              Privacy Policy:{" "}
              <a
                href="/apps/sqlvedit/privacy-policy"
                className="text-[#0F6BBF] underline"
              >
                cosmovexapps.com/apps/sqlvedit/privacy-policy
              </a>
            </li>
            <li>
              Support:{" "}
              <a
                href="/apps/sqlvedit/support"
                className="text-[#0F6BBF] underline"
              >
                cosmovexapps.com/apps/sqlvedit/support
              </a>
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            App Information
          </h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              <strong>App Name:</strong> SQL Viewer &amp; Editor
            </li>
            <li>
              <strong>Package:</strong> com.cosmovex.sqlvedit
            </li>
            <li>
              <strong>Developer:</strong> Cosmovex
            </li>
            <li>
              <strong>Contact:</strong>{" "}
              <a
                href="mailto:trionapps@gmail.com"
                className="text-[#0F6BBF] underline"
              >
                trionapps@gmail.com
              </a>
            </li>
          </ul>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-sm text-[#8A93A2]">
          &copy; 2026 Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
